import React, { useState, useEffect } from 'react';
import './App.css';

// Component for Welcome Screen
const WelcomeScreen = ({ salesperson, lang, onNext }) => {
  const [selectedValues, setSelectedValues] = useState([]);
  const [otherText, setOtherText] = useState('');
  const [product, setProduct] = useState('');

  const options = {
    en: {
      knowledge: 'Product Knowledge',
      care: 'Customer Care',
      professionalism: 'Professionalism',
      communication: 'Clear Communication',
      process: 'Fast and Easy Process',
      friendliness: 'Friendly Staff',
      financing: 'Financing Assistance',
      deal: 'Great Deal',
      test_drive: 'Test Drive Experience',
      delivery: 'Smooth Delivery',
      other: 'Other'
    },
    es: {
      knowledge: 'Conocimiento del producto',
      care: 'Atención al cliente',
      professionalism: 'Profesionalismo',
      communication: 'Comunicación clara',
      process: 'Proceso rápido y fácil',
      friendliness: 'Personal amable',
      financing: 'Asistencia con financiación',
      deal: 'Buena oferta',
      test_drive: 'Experiencia de prueba de manejo',
      delivery: 'Entrega sin inconvenientes',
      other: 'Otro'
    },
    hi: {
      knowledge: 'उत्पाद ज्ञान',
      care: 'ग्राहक सेवा',
      professionalism: 'व्यवसायिकता',
      communication: 'स्पष्ट संचार',
      process: 'तेज़ और आसान प्रक्रिया',
      friendliness: 'मित्रवत स्टाफ',
      financing: 'वित्तीय सहायता',
      deal: 'बेहतरीन सौदा',
      test_drive: 'टेस्ट ड्राइव अनुभव',
      delivery: 'सुव्यवस्थित डिलीवरी',
      other: 'अन्य'
    },
    ar: {
      knowledge: 'معرفة المنتج',
      care: 'خدمة العملاء',
      professionalism: 'الاحترافية',
      communication: 'تواصل واضح',
      process: 'عملية سريعة وسهلة',
      friendliness: 'طاقم ودود',
      financing: 'مساعدة في التمويل',
      deal: 'صفقة رائعة',
      test_drive: 'تجربة القيادة',
      delivery: 'تسليم سلس',
      other: 'أخرى'
    },
    ur: {
      knowledge: 'پروڈکٹ کا علم',
      care: 'کسٹمر سروس',
      professionalism: 'پیشہ ورانہ مہارت',
      communication: 'واضح رابطہ',
      process: 'تیز اور آسان عمل',
      friendliness: 'دوستانہ عملہ',
      financing: 'فنانسنگ میں مدد',
      deal: 'زبردست سودا',
      test_drive: 'ٹیسٹ ڈرائیو تجربہ',
      delivery: 'آسان ڈیلیوری',
      other: 'دیگر'
    },
    fr: {
      knowledge: 'Connaissance du produit',
      care: 'Service client',
      professionalism: 'Professionnalisme',
      communication: 'Communication claire',
      process: 'Processus rapide et facile',
      friendliness: 'Personnel amical',
      financing: 'Aide au financement',
      deal: 'Bonne affaire',
      test_drive: 'Expérience d’essai routier',
      delivery: 'Remise fluide',
      other: 'Autre'
    }
  };

  const texts = {
    en: {
      welcome: `Welcome! ${salesperson?.pref_name || 'Our team'} would love your feedback`,
      whatLiked: 'What did you like most about your experience?',
      productLabel: 'What product or service did you receive?',
      productPlaceholder: 'Example: 2023 Kona Preferred',
      otherPlaceholder: 'Example: I loved how quickly everything was handled.',
      generateBtn: 'Generate Review'
    },
    es: {
      welcome: `¡Bienvenido! A ${salesperson?.pref_name || 'nuestro equipo'} le encantaría recibir tus comentarios`,
      whatLiked: '¿Qué fue lo que más te gustó de tu experiencia?',
      productLabel: '¿Qué producto o servicio recibiste?',
      productPlaceholder: 'Ejemplo: 2023 Kona Preferred',
      otherPlaceholder: 'Ejemplo: Me encantó lo rápido que se manejó todo.',
      generateBtn: 'Generar Reseña'
    }
  };

  const currentTexts = texts[lang] || texts.en;
  const currentOptions = options[lang] || options.en;

  const handleCheckboxChange = (value) => {
    setSelectedValues(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const handleSubmit = async () => {
    if (selectedValues.length === 0 || !product.trim()) {
      alert(lang === 'es' ? 'Por favor completa todos los campos' : 'Please fill in all fields');
      return;
    }

    const formData = {
      seller: new URLSearchParams(window.location.search).get('seller'),
      lang,
      selectedValues: selectedValues.includes('other') ?
        [...selectedValues.filter(v => v !== 'other'), otherText] : selectedValues,
      product: product.trim(),
      timestamp: new Date().toISOString()
    };

    // Log interaction
    try {
      await fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          action: 'form_submitted'
        })
      });
    } catch (error) {
      console.error('Logging error:', error);
    }

    onNext(formData);
  };

  return (
    <div className="welcome-screen">
      <div className="header-banner">
        <img
          src="/api/placeholder/1200/300"
          alt="Mississauga Hyundai"
          className="banner-image"
        />
        <div className="welcome-overlay">
          <h1>{currentTexts.welcome}</h1>
        </div>
      </div>

      <div className="form-container">
        <h2>{currentTexts.whatLiked}</h2>

        <div className="checkbox-group">
          {Object.entries(currentOptions).map(([key, label]) => (
            <div key={key} className="checkbox-item">
              <label>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(key)}
                  onChange={() => handleCheckboxChange(key)}
                />
                {label}
              </label>
              {key === 'other' && selectedValues.includes('other') && (
                <input
                  type="text"
                  className="other-input"
                  placeholder={currentTexts.otherPlaceholder}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                />
              )}
            </div>
          ))}
        </div>

        <div className="product-input-group">
          <label>{currentTexts.productLabel}</label>
          <input
            type="text"
            placeholder={currentTexts.productPlaceholder}
            value={product}
            onChange={(e) => setProduct(e.target.value)}
          />
        </div>

        <button
          className="generate-btn"
          onClick={handleSubmit}
        >
          {currentTexts.generateBtn}
        </button>
      </div>
    </div>
  );
};

// Component for Review Selection Screen
const ReviewScreen = ({ formData, onBack, onReviewSelect }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState('');
  const [copied, setCopied] = useState(false);

  const texts = {
    en: {
      title: 'Choose Your Review',
      loading: 'Generating personalized reviews...',
      selectOne: 'Click on a review to select and copy it:',
      leaveReview: 'Leave Review on Google',
      back: 'Back'
    },
    es: {
      title: 'Elige Tu Reseña',
      loading: 'Generando reseñas personalizadas...',
      selectOne: 'Haz clic en una reseña para seleccionarla y copiarla:',
      leaveReview: 'Dejar Reseña en Google',
      back: 'Volver'
    }
  };

  const currentTexts = texts[formData.lang] || texts.en;

  useEffect(() => {
    generateReviews();
  }, []);

  const generateReviews = async () => {
    try {
      const response = await fetch('/api/generate-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.reviews) {
        setReviews(data.reviews);
        
        // Log successful generation
        await fetch('/api/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            action: 'reviews_generated',
            reviewCount: data.reviews.length
          })
        });
      }
    } catch (error) {
      console.error('Error generating reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSelect = async (review) => {
    setSelectedReview(review);

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(review);
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);

      // Log selection
      await fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          action: 'review_selected',
          selectedReview: review.substring(0, 100) + '...'
        })
      });
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const handleLeaveReview = async () => {
    // Log review submission
    try {
      await fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          action: 'redirected_to_google'
        })
      });
    } catch (error) {
      console.error('Logging error:', error);
    }

    // Set flag for thank you screen
    sessionStorage.setItem('reviewSubmitted', 'true');

    // Redirect to Google Reviews
    window.open('https://qrplanet.com/jzbwc6');   // Pointing to https://search.google.com/local/writereview?placeid=ChIJrVg-u75DK4gRQewqFg9Gb1U
  };

  if (loading) {
    return (
      <div className="review-screen">
        <div className="loading">
          <h2>{currentTexts.loading}</h2>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="review-screen">
      <h2>{currentTexts.title}</h2>
      <p>{currentTexts.selectOne}</p>

      {copied && (
        <div className="success-message">
          {formData.lang === 'es' ? '¡Copiado al portapapeles!\nPor favor, pega la reseña que generamos para ti y danos una calificación de 5 estrellas — ¡gracias!' : 'Copied to clipboard!\nPlease paste the review we generated for you and give us a 5-star rating — thank you!'}
        </div>
      )}

      <div className="reviews-container">
        {reviews.map((review, index) => (
          <div
            key={index}
            className={`review-option ${selectedReview === review ? 'selected' : ''}`}
            onClick={() => handleReviewSelect(review)}
          >
            {review}
          </div>
        ))}
      </div>

      <div className="button-group">
        <button className="back-btn" onClick={onBack}>
          {currentTexts.back}
        </button>
        <button
          className="leave-review-btn"
          disabled={!selectedReview}
          onClick={handleLeaveReview}
        >
          {currentTexts.leaveReview}
        </button>
      </div>
    </div>
  );
};

// Component for Thank You Screen
const ThankYouScreen = ({ lang }) => {
  const texts = {
    en: {
      title: 'Thank You!',
      message: 'Thank you for leaving your review! Your feedback means a lot to us and helps other customers make informed decisions.',
      subtitle: 'We appreciate your time and trust in Mississauga Hyundai.'
    },
    es: {
      title: '¡Gracias!',
      message: '¡Gracias por dejar tu reseña! Tus comentarios significan mucho para nosotros y ayudan a otros clientes a tomar decisiones informadas.',
      subtitle: 'Apreciamos tu tiempo y confianza en Mississauga Hyundai.'
    }
  };

  const currentTexts = texts[lang] || texts.en;

  return (
    <div className="thank-you-screen">
      <div className="thank-you-content">
        <h1>{currentTexts.title}</h1>
        <p className="thank-you-message">{currentTexts.message}</p>
        <p className="thank-you-subtitle">{currentTexts.subtitle}</p>
        <div className="thank-you-icon">⭐⭐⭐⭐⭐</div>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [salesperson, setSalesperson] = useState(null);
  const [lang, setLang] = useState('en');
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const seller = urlParams.get('seller') || '';
    const language = urlParams.get('lang') || 'en';

    setLang(language);

    // Fetch salesperson data
    if (seller) {
      fetchSalesperson(seller);
    }

    // Check if returning from Google Reviews
    const reviewSubmitted = sessionStorage.getItem('reviewSubmitted');
    if (reviewSubmitted) {
      setCurrentScreen('thankyou');
      sessionStorage.removeItem('reviewSubmitted');
    }
  }, []);

  const fetchSalesperson = async (seller) => {
    try {
      const response = await fetch(`/api/salesperson/${seller}`);
      if (response.ok) {
        const data = await response.json();
        setSalesperson(data);
      }
    } catch (error) {
      console.error('Error fetching salesperson:', error);
    }
  };

  const handleNext = (data) => {
    setFormData(data);
    setCurrentScreen('review');
  };

  const handleBack = () => {
    setCurrentScreen('welcome');
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'welcome':
        return (
          <WelcomeScreen
            salesperson={salesperson}
            lang={lang}
            onNext={handleNext}
          />
        );
      case 'review':
        return (
          <ReviewScreen
            formData={formData}
            onBack={handleBack}
            onReviewSelect={() => { }}
          />
        );
      case 'thankyou':
        return <ThankYouScreen lang={lang} />;
      default:
        return (
          <WelcomeScreen
            salesperson={salesperson}
            lang={lang}
            onNext={handleNext}
          />
        );
    }
  };

  return (
    <div className="App">
      {renderScreen()}
    </div>
  );
};

export default App;